import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Space, Input, Select, Tag, Modal, Tooltip,
  Badge, Progress, DatePicker, Typography, Alert,
} from 'antd';
import {
  SendOutlined, EyeOutlined, ReloadOutlined, CloseCircleOutlined,
  CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined,
  StopOutlined, FilterOutlined, TagOutlined, GroupOutlined, TeamOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchPushHistoryGrouped,
  cancelScheduledPush,
  selectGroupedItems,
  selectGroupedTotal,
  selectPushLoading,
  selectSegments,
} from 'store/slices/push.slice';
import { selectCurrentUser } from 'store/slices/auth.slice';
import { usePermissions } from 'hooks/usePermissions';
import {
  PushMessage, PushStatus, PushType, PushHistoryFilter,
  PushHistoryGroupedItem, PushMessageGroup,
} from 'types/push.types';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';
import { DEFAULT_PAGINATION } from 'utils/constants';
import { ROUTES } from 'config/routes';

const { Search } = Input;
const { RangePicker } = DatePicker;
const { confirm } = Modal;
const { Text } = Typography;

interface Props {
  appId: number | null;
  isAccManager?: boolean;
  segmentId?: number | null;
}

interface TableRow {
  key: string;
  is_group: boolean;
  title: string;
  body: string;
  push_type: PushType;
  sent_count: number;
  delivered_count: number;
  clicked_count: number;
  target_users_count: number;
  status?: PushStatus;
  sent_at?: string;
  scheduled_at?: string;
  segment_names: string[];
  group?: PushMessageGroup;
  count?: number;
  statuses?: string[];
  push?: PushMessage;
  message_id?: string;
}

const toTableRow = (item: PushHistoryGroupedItem): TableRow => {
  if (item.is_group && item.group) {
    const g = item.group;
    return {
      key: `group_${g.group_key}_${g.push_type}`,
      is_group: true,
      title: g.title,
      body: g.body,
      push_type: g.push_type,
      sent_count: g.total_sent,
      delivered_count: g.total_delivered,
      clicked_count: g.total_clicked,
      target_users_count: g.total_target_users,
      sent_at: g.last_sent_at || undefined,
      group: g,
      count: g.count,
      statuses: g.statuses,
      segment_names: g.segment_names || [],
    };
  }

  const p = item.push!;
  return {
    key: `push_${p.message_id}`,
    is_group: false,
    title: p.title,
    body: p.body,
    push_type: p.push_type,
    sent_count: p.sent_count,
    delivered_count: p.delivered_count,
    clicked_count: p.clicked_count,
    target_users_count: p.target_users_count,
    status: p.status,
    sent_at: p.sent_at,
    scheduled_at: p.scheduled_at,
    push: p,
    message_id: p.message_id,
    segment_names: p.segment_name ? [p.segment_name] : [],
  };
};

const PushList: React.FC<Props> = ({ appId, isAccManager = false, segmentId = null }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();

  const groupedItems = useAppSelector(selectGroupedItems);
  const total = useAppSelector(selectGroupedTotal);
  const isLoading = useAppSelector(selectPushLoading);
  const currentUser = useAppSelector(selectCurrentUser);
  const segments = useAppSelector(selectSegments);

  const [filters, setFilters] = useState<PushHistoryFilter>({
    limit: DEFAULT_PAGINATION.pageSize || 20,
    offset: 0,
  });
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

  const hasNoSegment = isAccManager && !segmentId;
  const historyFrom =
    permissions.isViewer && currentUser?.created_at
      ? dayjs(currentUser.created_at)
      : null;

  useEffect(() => {
    setFilters(prev =>
      prev.segment_id === undefined ? prev : { ...prev, segment_id: undefined, offset: 0 }
    );
  }, [appId]);

  useEffect(() => {
    const request = loadData();
    return () => request?.abort();
  }, [filters, appId, isAccManager, segmentId]);

  const historyQuery = (): PushHistoryFilter | null => {
    if (isAccManager) {
      return segmentId ? { ...filters, segment_id: segmentId } : null;
    }
    return appId ? { ...filters, app_id: appId } : null;
  };

  const loadData = () => {
    const query = historyQuery();
    return query ? dispatch(fetchPushHistoryGrouped(query)) : undefined;
  };

  const handleFilterChange = (key: keyof PushHistoryFilter, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value || undefined, offset: 0 }));
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates) {
      setDateRange(dates);
      setFilters(prev => ({
        ...prev,
        date_from: dates[0]?.startOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
        date_to: dates[1]?.endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
        offset: 0,
      }));
    } else {
      setDateRange([null, null]);
      setFilters(prev => {
        const { date_from, date_to, ...rest } = prev;
        return { ...rest, offset: 0 };
      });
    }
  };

  const handleCancelPush = (message: PushMessage) => {
    confirm({
      title: 'Отмена отправки',
      content: `Вы уверены, что хотите отменить отправку "${message.title}"?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Отменить отправку',
      cancelText: 'Назад',
      okType: 'danger',
      onOk: async () => {
        try {
          await dispatch(cancelScheduledPush(message.message_id)).unwrap();
          notify.success('Push-уведомление успешно отменено');
          loadData();
        } catch (error: any) {
          notify.error(error.message || 'Не удалось отменить отправку');
        }
      },
    });
  };

  const getStatusTag = (status: PushStatus) => {
    const config: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      [PushStatus.SCHEDULED]: { color: 'blue', icon: <ClockCircleOutlined />, label: 'Запланировано' },
      [PushStatus.SENDING]: { color: 'processing', icon: <SendOutlined />, label: 'Отправляется' },
      [PushStatus.SENT]: { color: 'success', icon: <CheckCircleOutlined />, label: 'Отправлено' },
      [PushStatus.FAILED]: { color: 'error', icon: <CloseCircleOutlined />, label: 'Ошибка' },
      [PushStatus.CANCELLED]: { color: 'default', icon: <StopOutlined />, label: 'Отменено' },
    };
    const c = config[status];
    if (!c) return <Tag>{status}</Tag>;
    return <Tag icon={c.icon} color={c.color}>{c.label}</Tag>;
  };

  const getGroupStatusTags = (statuses: string[]) => (
    <Space size={2} wrap>
      {statuses.map(s => getStatusTag(s as PushStatus))}
    </Space>
  );

  const getPushTypeTag = (type: PushType) => {
    const config: Record<string, { color: string; label: string }> = {
      [PushType.REGULAR]: { color: 'blue', label: 'Обычный' },
      [PushType.EVENT]: { color: 'purple', label: 'Событийный' },
      [PushType.SPECIAL]: { color: 'gold', label: 'Специальный' },
    };
    const c = config[type];
    return <Tag color={c?.color}>{c?.label || type}</Tag>;
  };

  const getDeliveryRate = (row: TableRow): number => {
    if (row.sent_count === 0) return 0;
    return (row.delivered_count / row.sent_count) * 100;
  };

  const getCTR = (row: TableRow): number => {
    if (row.delivered_count === 0) return 0;
    return (row.clicked_count / row.delivered_count) * 100;
  };

  const handleRowClick = (row: TableRow) => {
    if (row.is_group && row.group) {
      navigate(
        ROUTES.PUSH_GROUP_DETAIL
          .replace(':groupKey', row.group.group_key),
        {
          state: {
            group: row.group,
            appId,
            segmentId,
            filters,
          },
        }
      );
    } else if (row.message_id) {
      navigate(ROUTES.PUSH_DETAIL.replace(':messageId', row.message_id));
    }
  };

  const segmentColumn: ColumnsType<TableRow>[number] = {
    title: 'Сегмент',
    key: 'segment',
    width: 200,
    render: (_, record) => {
      const names = record.segment_names || [];
      if (names.length === 0) {
        return <span style={{ color: 'var(--text-muted)' }}>Все пользователи</span>;
      }
      if (names.length === 1) {
        return <Tag icon={<TeamOutlined />} color="cyan">{names[0]}</Tag>;
      }
      return (
        <Tooltip title={names.join(', ')}>
          <Space size={2} wrap>
            <Tag icon={<TeamOutlined />} color="cyan">{names[0]}</Tag>
            <Tag>+{names.length - 1}</Tag>
          </Space>
        </Tooltip>
      );
    },
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Заголовок',
      dataIndex: 'title',
      key: 'title',
      width: 280,
      render: (title: string, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 500 }}>{title}</span>
            {record.is_group && (
              <Tag color="geekblue" style={{ marginLeft: 4 }}>
                <GroupOutlined /> ×{record.count}
              </Tag>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {formatters.truncate(record.body, 50)}
          </div>
        </div>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'push_type',
      key: 'push_type',
      width: 120,
      render: (type: PushType) => getPushTypeTag(type),
    },
    ...(isAccManager ? [] : [segmentColumn]),
    {
      title: 'Статус',
      key: 'status',
      width: 160,
      render: (_, record) => {
        if (record.is_group && record.statuses) {
          return getGroupStatusTags(record.statuses);
        }
        return record.status ? getStatusTag(record.status) : '-';
      },
    },
    {
      title: 'Охват',
      key: 'reach',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Tooltip title={`Отправлено: ${record.sent_count} из ${record.target_users_count}`}>
          <Badge
            count={formatters.number(record.sent_count)}
            style={{ backgroundColor: 'var(--success)' }}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Доставка',
      key: 'delivery',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const rate = getDeliveryRate(record);
        return record.sent_count > 0 ? (
          <Tooltip title={`Доставлено: ${record.delivered_count} из ${record.sent_count}`}>
            <Progress
              type="circle"
              percent={rate}
              size={40}
              format={(p) => `${p?.toFixed(0)}%`}
            />
          </Tooltip>
        ) : <span style={{ color: 'var(--text-muted)' }}>-</span>;
      },
    },
    {
      title: 'CTR',
      key: 'ctr',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const ctr = getCTR(record);
        return record.delivered_count > 0 ? (
          <Tooltip title={`Кликов: ${record.clicked_count} из ${record.delivered_count}`}>
            <Progress
              type="circle"
              percent={ctr}
              size={40}
              format={(p) => `${p?.toFixed(1)}%`}
              strokeColor={ctr > 10 ? 'var(--success)' : ctr > 5 ? 'var(--warning)' : 'var(--danger)'}
            />
          </Tooltip>
        ) : <span style={{ color: 'var(--text-muted)' }}>-</span>;
      },
    },
    {
      title: 'Время',
      key: 'sent_at',
      width: 160,
      render: (_, record) => {
        if (!record.is_group && record.status === PushStatus.SCHEDULED && record.scheduled_at) {
          return (
            <span style={{ color: 'var(--accent)' }}>
              {formatters.dateTime(record.scheduled_at)}
            </span>
          );
        }
        return record.sent_at ? formatters.dateTime(record.sent_at) : '-';
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title={record.is_group ? 'Подробнее о группе' : 'Подробнее'}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleRowClick(record)}
            />
          </Tooltip>
          {!record.is_group &&
            record.status === PushStatus.SCHEDULED &&
            permissions.canManagePush &&
            !isAccManager && (
              <Tooltip title="Отменить отправку">
                <Button
                  type="text"
                  danger
                  icon={<StopOutlined />}
                  onClick={() => handleCancelPush(record.push!)}
                />
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    const offset = (pagination.current - 1) * pagination.pageSize;
    setFilters(prev => ({
      ...prev,
      limit: pagination.pageSize,
      offset,
    }));
  };

  const tableData: TableRow[] = groupedItems.map(toTableRow);
  if (hasNoSegment) {
    return (
      <Alert
        type="warning"
        showIcon
        icon={<TagOutlined />}
        message="Сегменты не назначены"
        description="Для просмотра истории push-уведомлений необходимо, чтобы администратор назначил вам сегменты."
        style={{ marginTop: 24 }}
      />
    );
  }

  if (!isAccManager && !appId) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <SendOutlined style={{ fontSize: 48, color: 'var(--text-muted)', marginBottom: 16 }} />
        <div style={{ fontSize: 16, color: 'var(--text-secondary)', marginBottom: 8 }}>
          Выберите приложение для просмотра истории
        </div>
        <Text type="secondary">
          История push-уведомлений отображается для каждого приложения отдельно
        </Text>
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space wrap>
          <Search
            placeholder="Поиск по заголовку или тексту"
            allowClear
            style={{ width: 250 }}
            onSearch={handleSearch}
            onChange={(e) => { if (!e.target.value) handleSearch(''); }}
            prefix={<FilterOutlined />}
          />
          <Select
            placeholder="Статус"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => handleFilterChange('status', value)}
            options={[
              { label: 'Запланировано', value: PushStatus.SCHEDULED },
              { label: 'Отправляется', value: PushStatus.SENDING },
              { label: 'Отправлено', value: PushStatus.SENT },
              { label: 'Ошибка', value: PushStatus.FAILED },
              { label: 'Отменено', value: PushStatus.CANCELLED },
            ]}
          />
          <Select
            placeholder="Тип"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => handleFilterChange('push_type', value)}
            options={[
              { label: 'Обычный', value: PushType.REGULAR },
              { label: 'Событийный', value: PushType.EVENT },
              { label: 'Специальный', value: PushType.SPECIAL },
            ]}
          />
          {!isAccManager && (
            <Select
              placeholder="Сегмент"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 220 }}
              value={filters.segment_id}
              onChange={(value) => handleFilterChange('segment_id', value)}
              options={segments.map(s => ({
                label: `${s.name} (${s.user_count?.toLocaleString() ?? '?'})`,
                value: s.id,
              }))}
            />
          )}
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={['Дата от', 'Дата до']}
            disabledDate={historyFrom ? (d) => d.isBefore(historyFrom, 'day') : undefined}
          />
          {historyFrom && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              История доступна с {historyFrom.format('DD.MM.YYYY')}
            </Text>
          )}
        </Space>
        <Button icon={<ReloadOutlined />} onClick={loadData}>
          Обновить
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={tableData}
        rowKey="key"
        loading={isLoading}
        pagination={{
          current: (filters.offset || 0) / (filters.limit || 20) + 1,
          pageSize: filters.limit || 20,
          total,
          showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1600 }}
        onRow={(record) => ({
          style: record.is_group
            ? { background: 'var(--bg-hover)', cursor: 'pointer' }
            : { cursor: 'pointer' },
          onClick: () => handleRowClick(record),
        })}
      />
    </div>
  );
};

export default PushList;